/* eslint-disable max-len */
import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  OnDestroy,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GeneralMenuItem } from 'src/app/components/shared/models/interface/generalMenuItem.interface';
import { PermissionMenuService } from 'src/app/components/shared/sharedModules/permission/permission-menu.service';
import { PermissionService } from 'src/app/components/shared/sharedModules/permission/permission.service';
import { UtilsService } from 'src/app/components/shared/sharedServices/utils.service';
import { Actions } from 'src/app/components/shared/types/actions.type';
import { environment } from 'src/environments/environment';
import { LayoutMenuActions } from '../../layout.page.component.actions.enum';

@Component({
  selector: 'app-layout-page-menu-sidebar',
  templateUrl: './layout-page-menu-sidebar.component.html',
  styleUrls: ['./layout-page-menu-sidebar.component.sass'],
})
export class LayoutPageMenuSidebarComponent implements OnInit, OnDestroy {
  @Input() actionList: Actions<LayoutMenuActions, any>;
  @Input() helpLink: string;
  @ViewChild('musidebar') sidebar: ElementRef;
  @HostBinding('class') baseClass;
  menuList: GeneralMenuItem[];
  contextActive: string;
  contextSelected: string;
  showMenu: boolean;
  excludeTranslation = {
    snd: /same.*next/i,
    cedis: /cedis/i,
  };
  destroyEventFn: () => void;

  constructor(
    private permissionMenuService: PermissionMenuService,
    private router: Router,
    private translateService: TranslateService,
    private permissionService: PermissionService,
    private utilsService: UtilsService,
    private render: Renderer2
  ) {}

  async ngOnInit(): Promise<void> {
    await this.getMenuList();
    this.watchActions();

    this.destroyEventFn = this.render.listen('window', 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      const clasName = typeof target?.className === 'string' ? target?.className : '';
      if (
        !this.sidebar.nativeElement.contains(target) &&
        !clasName.match(/mu-icon-menu/) &&
        this.showMenu
      ) {
        this.actionList.emit(LayoutMenuActions.SHOW_MENU, null);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyEventFn();
  }

  /**
   * get menu list
   */
  async getMenuList(): Promise<void> {
    this.menuList = await this.permissionMenuService.getMenuModel();
    this.contextActive = this.menuList?.find(r =>
      r.items?.find(x => this.router?.url?.startsWith(x.routerLink[0]))
    )?.contextPath;

    if (this.contextActive) {
      return;
    }

    this.contextActive = this.menuList?.find(x => {
      if (!x.routerLink) {
        return false;
      }
      if (x.routerLink.length === 0) {
        return false;
      }
      return this.router?.url?.startsWith(x?.routerLink[0]);
    })?.contextPath;
  }

  /**
   * set selected context
   * @param {GeneralMenuItem} menuItem
   * @param {string[]} redirectLink
   */
  async setSelectedContext(menuItem: GeneralMenuItem, redirectLink?: string[]): Promise<void> {
    if (!menuItem.active) {
      return environment.guardClause;
    }

    this.contextSelected =
      (await this.isContextAvailable(menuItem.contextPath)) &&
      this.contextSelected !== menuItem.contextPath
        ? menuItem.contextPath
        : null;

    if (redirectLink) {
      this.router.navigate(redirectLink);
    }
  }

  /**
   * is context available
   * @param {string} contextPath
   * @returns {boolean} response
   */
  async isContextAvailable(contextPath: string): Promise<boolean> {
    const canAccess = await this.permissionService.canAccess(contextPath);
    const navigationExtras: NavigationExtras = {
      state: {
        data: this.translateService.instant('common.unauthorized.' + contextPath),
        contextPath,
      },
    };

    if (!canAccess) {
      this.router.navigate(['/acceso-denegado-servicio'], navigationExtras);
      return false;
    }

    return true;
  }

  /**
   * watch actions
   */
  watchActions(): void {
    this.actionList.on(LayoutMenuActions.SHOW_MENU, async () => {
      this.showMenu = !this.showMenu;
      this.baseClass = this.showMenu ? 'mu-menu-sidebar-show ' : ' ';
      await this.utilsService.muSleep(500);
    });
  }

  /**
   * Hide the sidebar after option clicking
   */
  hideSidebar() {
    if (this.showMenu) {
      this.actionList.emit(LayoutMenuActions.SHOW_MENU, null);
    }
  }
}
